import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }        from '../api/api.service';
import { BigDecimalService } from '../core/services/big-decimal.service';
import { CompanyService }    from '../companies/company.service';
import { ConfirmService }    from '../messages/confirm/confirm.service';
import { SessionService }    from '../sessions/session.service';
import { ProductBranchStockComponent } from './product.branch-stock.component';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  constructor (
    private apiService       : ApiService,
    private bigDecimalService: BigDecimalService,
    private companyService   : CompanyService,
    private confirmService   : ConfirmService,
    private modalService     : NgbModal,
    private sessionService   : SessionService,
    private translateService : TranslateService
  ) {}

  activate ( product : any ) : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.ACTIVATE_PRODUCT', {
      code: product.item_code
    }))
    .then(() => this.apiService.get('/products/reactivate_product', product.id));
  }

  calculateCostWithoutTax ( costIncludingTax : any, purchaseTaxRate : any ) : number {
    costIncludingTax = this.bigDecimalService.get(costIncludingTax, 4);

    const taxRate = this.bigDecimalService.get(purchaseTaxRate, 4).movePointLeft(2);
    const cost    = costIncludingTax.divide(BigDecimal.ONE.add(taxRate)).setScale(4, BigDecimal.prototype.ROUND_HALF_UP);

    return parseFloat(cost);
  }

  calculateCostWithTax ( cost : any, purchaseTaxRate : any ) : number {
    cost = this.bigDecimalService.get(cost, 4);

    const taxRate = this.bigDecimalService.get(purchaseTaxRate, 4).movePointLeft(2);
    const tax     = taxRate.multiply(cost).setScale(4, BigDecimal.prototype.ROUND_HALF_UP);

    return parseFloat(cost.add(tax).toString());
  }

  calculateQtyAvailable ( product : any ) : string {
    const quantityOnHand    = this.bigDecimalService.get(product.quantity_on_hand).setScale(2, BigDecimal.prototype.ROUND_HALF_UP);
    const quantityReserved  = this.bigDecimalService.get(product.qty_reserved).setScale(2, BigDecimal.prototype.ROUND_HALF_UP);
    const quantityAvailable = quantityOnHand.subtract(quantityReserved).setScale(2, BigDecimal.prototype.ROUND_HALF_UP);

    return parseFloat(quantityAvailable).toFixed(1);
  }

  findInHeadOffice ( product : any ) : Promise<any> {
    if (!this.companyService.isLinkedToBranch()) {
      return Promise.resolve(false);
    }

    return this.companyService.isDealerMultibranchStore()
    .then(( result : boolean ) => {
      return result
        ? this.apiService.post('/product/branch_find_on_dealer', product, 'product')
        .then(( product ) => {
          return product
            ? this.confirmService.generic('This product exists in the master database. Would you like to use these details?')
              .then(() => Promise.resolve(product))
            : Promise.resolve(false);
        })
        : Promise.resolve(false);
    });
  }

  formatSearchableTags ( product : any ) : any {
    if ( !product.searchable_tags ) {
      return product;
    }

    product.searchable_tags = product.searchable_tags.split(',');

    return product;
  }

  getBranchProducts ( product : any ) : Promise<Array<any>> {
    return this.apiService.post('/product/branch_find', {
      item_code: product.item_code
    });
  }

  getPurchaseTaxRate ( product : any ) : Promise<string> {
    const companyTaxRate = this.sessionService.currency().purchases_tax_rate;

    if ( !product.product_group_name || this.companyService.hasMultiTax() ) {

      return Promise.resolve(companyTaxRate);
    }

    return this.apiService.get('/product_group', product.product_group_name)
    .then(( group = [] ) => {
      return group.length && group[0].product_category_id
        ? this.apiService.get('/product_category', group[0].product_category_id)
          .then(category => category.purchase_tax_rate || companyTaxRate)
        : companyTaxRate;
    });
  }

  getSalesTaxRate ( product : any ) : Promise<string> {
    const companyTaxRate = this.sessionService.currency().sales_tax_rate;

    if ( !product.product_group_name || this.companyService.hasMultiTax() ) {

      return Promise.resolve(companyTaxRate);
    }

    return this.apiService.get('/product_group', product.product_group_name)
    .then(( group = [] ) => {
      return group.length && group[0].product_category_id
        ? this.apiService.get('/product_category', group[0].product_category_id)
          .then(category => category.sales_tax_rate || companyTaxRate)
        : companyTaxRate;
    });
  }

  getStockTransfers ( product : any ) : Promise<any> {
    return this.apiService.get('/products/stock_transfer_log_list', product.id);
  }

  hasLaborItemsAttributes ( product : any ) : boolean{
    return product.labor_items_attributes.length > 0;
  }

  isBomParent ( product : any ) : boolean {
    return product.is_bom_parent;
  }

  isLabor ( product : any ) : boolean {
    return product.product_type === 'J';
  }

  isLinkedToHeadOffice ( product : any ) : boolean {
    return !!product.branch_link;
  }

  pushDownPricingInfo ( product : any ) : Promise<any> {
    return this.apiService.get('/product/branch_push_down', product.id);
  }

  save ( product : any, form ?: any ) : Promise<any> {
    const url    = product.id ? '/product': '/products';
    const method = product.id ? 'patch': 'post';

    /**
     * Even though vendor_name is not technically a field
     * on products, it is manually added on the FE in both
     * places this function is called. Product save and
     * advanced search quick product add. So, if the vendor
     * name is blank, we know the user has cleared out the
     * vendor so we need to clear the ID to ensure the
     * vendor link gets removed.
     *
     * Really only needed for the product page because the
     * advanced search component already clears the ID
     * when the vendor is cleared.
     */
    if (!this.isLabor(product) && !product.vendor_name) {
      product.vendor_id = null;
    }

    return this.validateItemCode(product, form)
    .then(( isUnique : boolean ) => {
      return isUnique
        ? Promise.resolve(true)
        : this.confirmService.generic(this.translateService.instant('JS_SPACE.MESSAGES.PRODUCT_ALREADY_EXISTS', {
          code: product.item_code
        }));
    })
    .then(() => this.apiService[method](url, product, 'product'));
  }

  validateItemCode ( product : any, form ?: any ) : Promise<boolean> {
    if ( form && ( form.item_code?.$pristine || form.controls?.item_code?.pristine )) {
      return Promise.resolve(true);
    }

    return this.apiService.post('/product/verify_code_uniqueness', {
      item_code: product.item_code
    });
  }

  viewBranchStock ( product : any ) : Promise<void> {
    return this.getBranchProducts(product)
    .then(( products : Array<any> ) => {
      const modal = this.modalService.open(ProductBranchStockComponent, {
        windowClass: 'x-lg-modal'
      });

      modal.componentInstance.products = products;

      return modal.result;
    });
  }
}